import { useFormik } from "formik";
import React from "react";
import { Button, TextField, MenuItem, Box } from "@mui/material";
import { useMessagesPopup } from "../../contexts/MessagesPopupProvider/MessagesPopupProvider";
import { useAuth } from "../../contexts/AuthProvider/AuthProvider";
import { api } from "../../api/api";
import { Dialog } from "../../components/Dialogs/Dialog";
import { CustomFormLabel, CustomTextField } from "../../components/formsComponents";
import { PlanAutoSearch } from "../../components/AutoSearch/PlanAutoSearch";
import { FileUploadV2 } from "../../components/FileUpload/FileUploadV2";
import { uploadImage } from "../../components/FileUpload/utils";

export interface ICreateOfferDialog {
    open: boolean;
    onClose: () => void;
    mode: "create" | "edit";
    offer?: {
        offer_code: string | null;
        offer_name: string;
        offer_description: string;
        offer_type: string;
        image_url: string;
        order_no: number;
        package_id: number;
        package_json: Record<string, any>;
        promo_code: string;
        status: "ACTIVE" | "INACTIVE";
        comment: string;
    };
}

export const CreateOfferDialog: React.FC<ICreateOfferDialog> = ({
    open,
    onClose,
    offer,
    mode,
}) => {
    const [loading, setLoading] = React.useState(false);
    const { showSuccessMessage, showTopErrorSnackbar } = useMessagesPopup();
    const auth = useAuth();

    const { values, errors, handleChange, handleSubmit, setFieldValue, resetForm } = useFormik({
        initialValues: {
            offer_code: offer?.offer_code ?? null,
            offer_name: offer?.offer_name ?? "",
            offer_description: offer?.offer_description ?? "",
            offer_type: offer?.offer_type ?? "",
            image_url: offer?.image_url ?? "",
            order_no: offer?.order_no ?? 0,
            package_id: offer?.package_id ?? 0,
            package_json: offer?.package_json ?? {},
            promo_code: offer?.promo_code ?? "",
            status: offer?.status ?? "ACTIVE",
            comment: offer?.comment ?? "",
        },
        validate: (values) => {
            const errors: any = {};
            if (!values.offer_name.trim()) {
                errors.offer_name = "*Required field";
            }
            if (!values.offer_type) {
                errors.offer_type = "*Required field";
            }
            if (!values.status) {
                errors.status = "*Required field";
            }
            return errors;
        },
        onSubmit: async (values) => {
            try {
                setLoading(true);
                const payload = {
                    offer_code: values.offer_code === "" ? null : values.offer_code,
                    offer_name: values.offer_name,
                    offer_description: values.offer_description,
                    offer_type: values.offer_type,
                    image_url: values.image_url,
                    order_no: values.order_no,
                    package_id: values.package_id,
                    package_json: values.package_json,
                    promo_code: values.promo_code,
                    status: values.status,
                    comment: values.comment,
                };

                if (auth.user.tokens?.access) {
                    await api.post("/upsert-offer", payload, {
                        headers: {
                            "auth-Token": auth.user.tokens.access,
                        },
                    });

                    showSuccessMessage(
                        `Offer ${mode === "edit" ? "updated" : "created"} successfully`
                    );
                    onClose();
                    if (mode === "create") {
                        resetForm();
                    }
                }
            } catch (err: any) {
                showTopErrorSnackbar(err.response?.data?.msg || "Operation failed");
            } finally {
                setLoading(false);
            }
        },
    });

    return (
        <Dialog
            open={open}
            size="md"
            title={mode === "edit" ? "Edit Offer" : "Create Offer"}
            onClose={onClose}
            contentWrappedWithForm={{
                onSubmit: handleSubmit,
            }}
            actions={[
                {
                    label: mode === "edit" ? "Update" : "Create",
                    type: "submit",
                    disabled: loading,
                    variant: "contained",
                },
            ]}
        >
            {/* Offer Fields */}
            <CustomFormLabel>Offer Name</CustomFormLabel>
            <CustomTextField
                name="offer_name"
                value={values.offer_name}
                onChange={handleChange}
                error={!!errors.offer_name}
                helperText={errors.offer_name}
                fullWidth
            />

            <CustomFormLabel>Offer Description</CustomFormLabel>
            <CustomTextField
                name="offer_description"
                value={values.offer_description}
                onChange={handleChange}
                fullWidth
                multiline
                rows={2}
            />

            <CustomFormLabel>Offer Type</CustomFormLabel>
            <TextField
                name="offer_type"
                value={values.offer_type}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.offer_type}
                helperText={errors.offer_type}
            />

            <CustomFormLabel>Image URL</CustomFormLabel>
            <FileUploadV2
                value={values.image_url}
                onChange={async (file) => {
                    if (file) {
                        const uploaded = await uploadImage(file);
                        if (uploaded) {
                            // const baseURL = api.defaults.baseURL;
                            const fullImageUrl = `https://api.crossroadshelpline.com${uploaded}`;
                            setFieldValue("image_url", fullImageUrl);
                        } else {
                            setFieldValue("image_url", "");
                        }
                    } else {
                        setFieldValue("image_url", "");
                    }
                }}
            />

            <CustomFormLabel>Order No</CustomFormLabel>
            <CustomTextField
                name="order_no"
                type="number"
                value={values.order_no}
                onChange={handleChange}
                fullWidth
            />

            <CustomFormLabel>Select Plan</CustomFormLabel>
            {/* <CustomTextField
                name="package_id"
                type="number"
                value={values.package_id}
                onChange={handleChange}
                fullWidth
            /> */}
            <PlanAutoSearch
                initialValue={values.package_json?.package_name || null}
                onSelect={(value) => {
                    setFieldValue("package_id", value?.package_id ?? 0);
                    setFieldValue("package_json", value || {});
                }}
            />


            <CustomFormLabel>Promo Code</CustomFormLabel>
            <CustomTextField
                name="promo_code"
                value={values.promo_code}
                onChange={handleChange}
                fullWidth
            />

            <CustomFormLabel>Status</CustomFormLabel>
            <TextField
                select
                name="status"
                value={values.status}
                onChange={handleChange}
                fullWidth
                size="small"
                error={!!errors.status}
                helperText={errors.status}
            >
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="INACTIVE">Inactive</MenuItem>
            </TextField>

            <CustomFormLabel>Comment</CustomFormLabel>
            <CustomTextField
                name="comment"
                value={values.comment}
                onChange={handleChange}
                multiline
                rows={3}
                fullWidth
            />
        </Dialog>
    );
};